import { useState, type ReactElement } from 'react';
import { ActionIcon, Box, Button, Collapse, Group, Popover, Stack, Text, Tooltip } from '@mantine/core';
import { ChevronDown, ChevronRight, ListFilterPlus } from 'lucide-react';
import type { GlobalFilterField } from '@/features/global-filters/GlobalFilterTypes';
import type { AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { LocalFieldChecklist, PersonSearchChecklist, ProviderConnectionChecklist } from './FilterFieldEditors';

export interface AddFilterMenuProps {
    globalGroups: { group: string; fields: GlobalFilterField[] }[];
    localGroups: { group: string; fields: { value: string; label: string }[] }[];
    globalDims: AnalyticsFilter[];
    localFilters: AnalyticsFilter[];
    onToggleGlobal: (field: GlobalFilterField, value: string, checked: boolean) => void;
    onToggleLocal: (field: AnalyticsField, value: string, checked: boolean) => void;
    /** When set (no filters active), the trigger renders as a labeled chip (e.g. "Showing all") instead of the bare funnel icon. */
    emptyLabel?: string;
}

interface SectionHeaderProps {
    label: string;
    expanded: boolean;
    count: number;
    indent?: boolean;
    onClick: () => void;
}

function SectionHeader({ label, expanded, count, indent = false, onClick }: SectionHeaderProps): ReactElement {
    return (
        <Button
            variant="subtle"
            color="gray"
            size="compact-sm"
            fullWidth
            justify="space-between"
            pl={indent ? 'md' : 'xs'}
            onClick={onClick}
            rightSection={
                <Group gap={6} wrap="nowrap">
                    {count > 0 && (
                        <Text size="xs" c="var(--mantine-primary-color-filled)" fw={600}>
                            {count}
                        </Text>
                    )}
                    {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </Group>
            }
            styles={{ label: { fontWeight: indent ? 500 : 600 } }}
        >
            {label}
        </Button>
    );
}

/**
 * The "+ Add filter" dropdown: an accordion of filter groups. Global groups
 * (Providers, People) expand straight into their value checklist — individual
 * provider connections, a person search. Local groups expand into their fields,
 * and a field expands one level further into its value checklist. Every
 * checkbox applies immediately; the menu doubles as an editor since checked
 * state mirrors the live filters.
 */
export default function AddFilterMenu({ globalGroups, localGroups, globalDims, localFilters, onToggleGlobal, onToggleLocal, emptyLabel }: AddFilterMenuProps): ReactElement {
    const [opened, setOpened] = useState(false);
    const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
    const [expandedField, setExpandedField] = useState<string | null>(null);

    const close = (): void => {
        setOpened(false);
        setExpandedGroup(null);
        setExpandedField(null);
    };
    const toggleGroup = (key: string): void => {
        setExpandedGroup((current) => (current === key ? null : key));
        setExpandedField(null);
    };

    const selectedFor = (field: string): string[] =>
        globalDims.find((filter) => filter.field === field)?.values ?? localFilters.find((filter) => filter.field === field)?.values ?? [];

    const globalEditor = (field: GlobalFilterField): ReactElement =>
        field === 'provider_connection_id' ? (
            <ProviderConnectionChecklist selected={selectedFor(field)} onToggle={(value, checked) => onToggleGlobal(field, value, checked)} />
        ) : (
            <PersonSearchChecklist selected={selectedFor(field)} onToggle={(value, checked) => onToggleGlobal(field, value, checked)} />
        );

    return (
        <Popover opened={opened} onChange={(next) => (next ? setOpened(true) : close())} position="bottom-end" width={320} shadow="md" radius="md" withinPortal trapFocus>
            <Popover.Target>
                <Tooltip label="Add filter" withArrow disabled={opened}>
                    {emptyLabel !== undefined ?
                        <Button
                            size="sm"
                            radius="md"
                            variant="default"
                            fw={500}
                            c="dimmed"
                            leftSection={<ListFilterPlus size={15} />}
                            aria-label="Add filter"
                            onClick={() => (opened ? close() : setOpened(true))}
                        >
                            {emptyLabel}
                        </Button>
                    :   <ActionIcon size={36} radius="md" variant="default" aria-label="Add filter" onClick={() => (opened ? close() : setOpened(true))}>
                            <ListFilterPlus size={17} />
                        </ActionIcon>
                    }
                </Tooltip>
            </Popover.Target>
            <Popover.Dropdown p="xs">
                <Stack gap={2}>
                    {globalGroups.map((group) =>
                        group.fields.map((field) => (
                            <Box key={field}>
                                <SectionHeader label={group.group} expanded={expandedGroup === field} count={selectedFor(field).length} onClick={() => toggleGroup(field)} />
                                <Collapse in={expandedGroup === field}>
                                    {expandedGroup === field && (
                                        <Box px="xs" py={6}>
                                            {globalEditor(field)}
                                        </Box>
                                    )}
                                </Collapse>
                            </Box>
                        )),
                    )}
                    {localGroups.map((group) => (
                        <Box key={group.group}>
                            <SectionHeader
                                label={group.group}
                                expanded={expandedGroup === group.group}
                                count={group.fields.reduce((total, field) => total + selectedFor(field.value).length, 0)}
                                onClick={() => toggleGroup(group.group)}
                            />
                            <Collapse in={expandedGroup === group.group}>
                                {expandedGroup === group.group && (
                                    <Stack gap={2} py={2}>
                                        {group.fields.map((field) => (
                                            <Box key={field.value}>
                                                <SectionHeader
                                                    label={field.label}
                                                    indent
                                                    expanded={expandedField === field.value}
                                                    count={selectedFor(field.value).length}
                                                    onClick={() => setExpandedField((current) => (current === field.value ? null : field.value))}
                                                />
                                                <Collapse in={expandedField === field.value}>
                                                    {expandedField === field.value && (
                                                        <Box px="md" py={6}>
                                                            <LocalFieldChecklist
                                                                field={field.value as AnalyticsField}
                                                                selected={selectedFor(field.value)}
                                                                onToggle={(value, checked) => onToggleLocal(field.value as AnalyticsField, value, checked)}
                                                            />
                                                        </Box>
                                                    )}
                                                </Collapse>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Collapse>
                        </Box>
                    ))}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}
